import React, { useState, useEffect, useContext } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import Select from 'react-select';
import { toast, ToastContainer } from 'react-toastify'; // Toast notifications
import 'react-toastify/dist/ReactToastify.css';
import OrgContext from '../context/OrgContext';

const Products = () => {

    const { activeOrg } = useContext(OrgContext);

    const [products, setProducts] = useState([]);
    const [newProduct, setNewProduct] = useState({ name: '', description: '', image: '', price: 0, articles: [], category: '' });
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [articles, setArticles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [quantity, setQuantity] = useState('');
    const [previewImage, setPreviewImage] = useState('');
    const [profitPercentage, setProfitPercentage] = useState(0); // Profit calculation state
    const [selectedUnit, setSelectedUnit] = useState(''); // To store the selected article's unit

    useEffect(() => {
        fetchCategories();
        fetchArticles();
        fetchProducts();
    }, [activeOrg]);

    useEffect(() => {
        const calculateCostAndProfit = () => {
            // Ensure articles and product structure are initialized
            if (!articles || articles.length === 0 || !newProduct.articles) return;
    
            // Calculate total cost by iterating over newProduct.articles
            const totalCost = newProduct.articles.reduce((acc, art) => {
                // Find the matching article in the articles list
                const articleDetails = articles.find(article => article._id === art.article);
    
                if (!articleDetails || !articleDetails.price || !articleDetails.qty) return acc;
    
                // Calculate the cost per item based on the specified quantity
                const articleCost = (art.quantity / articleDetails.qty) * articleDetails.price;
                return acc + articleCost;
            }, 0);
    
            // Calculate profit percentage
            const profitPercentage = totalCost > 0 ? ((newProduct.price - totalCost) / totalCost) * 100 : 0;
    
            // Update the product's cost and profit percentage state
            setNewProduct(prevProduct => ({
                ...prevProduct,
                cost: totalCost.toFixed(2),
            }));
            setProfitPercentage(profitPercentage.toFixed(0));
        };
    
        // Trigger calculation only if necessary data is loaded
        if (newProduct.price > 0 && newProduct.articles.length > 0 && articles.length > 0) {
            calculateCostAndProfit();
        } else {
            setNewProduct(prevProduct => ({ ...prevProduct, cost: 0 }));
            setProfitPercentage(0);
        }
    }, [newProduct.price, newProduct.articles, articles]);
    
    
    const fetchProducts = async () => {
        if (!activeOrg || !activeOrg._id) {
            console.warn("Active organization is not set. Skipping fetchProducts.");
            return;
        }
        const token = sessionStorage.getItem('token');
        try {
            console.log("Fetching products for org:", activeOrg._id); // Debugging log to confirm org ID
            const response = await axios.get(`${process.env.REACT_APP_SRV}/products`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    orgid: activeOrg._id // Place orgid inside headers
                },
            });
            setProducts(response.data || []);
        } catch (error) {
            console.error('Error fetching products:', error);
            setProducts([]); // Reset or handle error state
        }
    };
    

    const fetchArticles = async () => {
        const token = sessionStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_SRV}/articles`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    orgid: `${activeOrg ? activeOrg._id : ''}` // Place orgid inside headers
                },
            });
            setArticles(response.data);
        } catch (error) {
            console.error('Error fetching articles:', error);
        }
    };

    const fetchCategories = async () => {
        const token = sessionStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_SRV}/categories`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    orgid: `${activeOrg ? activeOrg._id : ''}` // Place orgid inside headers
                },
            });
            setCategories(response.data);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    // Trigger cost and profit recalculation on adding or removing articles
    const handleAddArticle = () => {
        if (!selectedArticle || !quantity) {
            toast.error('Please select an article and enter a quantity.');
            return;
        }

        const existingArticleIndex = newProduct.articles.findIndex(
            articles => articles.article === selectedArticle.value
        );

        if (existingArticleIndex !== -1) {
            const updatedArticles = [...newProduct.articles];
            updatedArticles[existingArticleIndex].quantity =
                parseFloat(updatedArticles[existingArticleIndex].quantity) + parseFloat(quantity);
            setNewProduct((prevProduct) => ({
                ...prevProduct,
                articles: updatedArticles,
            }));
        } else {
            setNewProduct((prevProduct) => ({
                ...prevProduct,
                articles: [
                    ...newProduct.articles,
                    { article: selectedArticle.value, quantity: parseFloat(quantity) }
                ],
            }));
        }

        setSelectedArticle(null);
        setQuantity('');
    };

    const handleRemoveArticle = (articleID) => {
        setNewProduct((prevProduct) => {
            const updatedArticles = prevProduct.articles.filter(articles => articles.article !== articleID);
            return { ...prevProduct, articles: updatedArticles };
        });
    };

    const onDrop = async (acceptedFiles) => {
        const file = acceptedFiles[0];
        setPreviewImage(URL.createObjectURL(file));

        const token = sessionStorage.getItem('token');
        const formData = new FormData();
        formData.append('image', file);

        try {
            const response = await axios.post(`${process.env.REACT_APP_SRV}/products/upload`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            const imageUrl = response.data.imageUrl;
            setNewProduct({ ...newProduct, image: imageUrl });
        } catch (error) {
            console.error('Error uploading image:', error);
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
        multiple: false,
    });


    const openDialog = (product) => {
        if (product) {
            // Set newProduct with the product data, including the cost
            setNewProduct({ ...product });
            setPreviewImage(product.image ? `${process.env.REACT_APP_SRV}${product.image}` : '');

            // Set profit percentage based on the saved cost and price
            const totalCost = product.cost || 0; // Use saved cost from the product
            const profit = totalCost > 0 ? ((product.price - totalCost) / totalCost) * 100 : 0;
            setProfitPercentage(profit.toFixed(0));
        } else {
            // If creating a new product, reset newProduct state
            setNewProduct({ name: '', description: '', image: '', price: 0, cost: 0, articles: [], category: '' });
            setPreviewImage('');
            setProfitPercentage(0); // Reset profit percentage for a new product
        }
        setIsDialogOpen(true);
    };


    const closeDialog = () => {
        setIsDialogOpen(false);
        setPreviewImage('');
    };

    const handleSelectArticle = (selectedOption) => {
        const article = articles.find(ing => ing._id === selectedOption.value);
        setSelectedArticle(selectedOption);
        setSelectedUnit(article ? article.unit : ''); // Set the unit when selecting an article
    };

    const handleCreateOrUpdate = async () => {
        const token = sessionStorage.getItem('token');
        try {
            const endpoint = newProduct._id
                ? `${process.env.REACT_APP_SRV}/products/${newProduct._id}`
                : `${process.env.REACT_APP_SRV}/products`;
            const method = newProduct._id ? 'put' : 'post';

            const articlesPayload = newProduct.articles.map(ing => ({
                article: typeof ing.article === 'object' ? ing.article._id : ing.article,
                quantity: ing.quantity,
            }));

            const productPayload = {
                name: newProduct.name,
                description: newProduct.description,
                image: newProduct.image,
                price: newProduct.price,
                cost: newProduct.cost,
                vat: newProduct.vat,
                articles: articlesPayload,
                category: newProduct.category,
            };

            const response = await axios[method](endpoint, productPayload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    orgid: `${activeOrg ? activeOrg._id : ''}` // Include JWT token in headers
                },
            });

            const { product, message } = response.data;

            const inList = products.some(r => r._id === product._id);

            if (inList) {
                // Replace the existing product in the array
                setProducts(products.map(r => r._id === product._id ? product : r));
            } else {
                // Add new product to the array
                setProducts([...products, product]);
            }

            toast.info(message); // Display message in toast
            closeDialog();
        } catch (error) {
            toast.error(`Error ${newProduct._id ? 'updating' : 'creating'} product: ${error.response?.data?.message || error.message}`);
        }
    };

    const handleDelete = async (id) => {
        const token = sessionStorage.getItem('token');
        try {
            await axios.delete(`${process.env.REACT_APP_SRV}/products/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    orgid: `${activeOrg ? activeOrg._id : ''}` // Include JWT token in headers
                },
            });
            setProducts(products.filter(product => product._id !== id));
            toast.success('Product deleted successfully!');
        } catch (error) {
            toast.error('Error deleting product');
        }
    };

    return (
        <div>
            <ToastContainer />
            <div className="mb-4 flex justify-between items-center">
                <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="px-4 py-2 bg-gray-800 rounded text-white"
                />
                <button onClick={() => openDialog(null)} className="bg-green-600 px-4 py-2 rounded hover:bg-green-700">
                    Add Product
                </button>
            </div>

            <ul>
                {products
                    .filter((product) => {
                        const lowercasedQuery = searchQuery.toLowerCase();
                        return (
                            product.name.toLowerCase().includes(lowercasedQuery) ||
                            product.description.toLowerCase().includes(lowercasedQuery)
                        );
                    })
                    .map(product => (
                        <li key={product._id} className="bg-gray-800 p-4 mb-2 rounded flex justify-between items-start">
                            <div className="flex items-start">
                                {product.image && (
                                    <img
                                        src={`${process.env.REACT_APP_SRV}${product.image}`}
                                        alt={product.name}
                                        className="w-16 h-16 rounded mr-4"
                                    />
                                )}
                                <div className="flex flex-col">
                                    <h3 className="text-xl">{product.name}</h3>
                                    <p className="text-gray-400">{product.description}</p>
                                    <p className="text-gray-400">Price: {product.price}</p>
                                </div>
                            </div>

                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => openDialog(product)}
                                    className="bg-blue-600 px-4 py-2 rounded hover:bg-blue-700"
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleDelete(product._id)}
                                    className="bg-red-600 px-4 py-2 rounded hover:bg-red-700"
                                >
                                    Delete
                                </button>
                            </div>
                        </li>
                    ))}
            </ul>

            {isDialogOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center">
                    <div className="bg-gray-800 p-6 rounded-lg w-2/3">
                        <h3 className="text-xl mb-4">{newProduct._id ? 'Edit Product' : 'Add New Product'}</h3>

                        {/* Category, Name, Price, and Profit on the next line */}
                        <div className="mb-4 flex flex-col space-y-4">
                            <div className="flex space-x-4 items-center">
                                <div className="w-1/4">
                                    <label className="text-white mb-1 block">Category</label>
                                    <select
                                        value={newProduct.category}
                                        onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                                        className="block w-full p-2 bg-gray-700 text-white rounded"
                                    >
                                        <option value="">Select Category</option>
                                        {categories.map(category => (
                                            <option key={category._id} value={category._id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="w-1/4">
                                    <label className="text-white mb-1 block">Name</label>
                                    <input
                                        type="text"
                                        placeholder="Name"
                                        value={newProduct.name}
                                        onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                                        className="block w-full p-2 bg-gray-700 text-white rounded"
                                    />
                                </div>

                                <div className="w-1/4">
                                    <label className="text-white mb-1 block">Price</label>
                                    <input
                                        id="price"
                                        type="number"
                                        placeholder="Price"
                                        value={newProduct.price === 0 ? '' : newProduct.price}
                                        onChange={(e) => {
                                            const updatedPrice = parseFloat(e.target.value) || 0;
                                            setNewProduct(prevProduct => ({ ...prevProduct, price: updatedPrice }));
                                        }}
                                        className="block w-full p-2 bg-gray-700 text-white rounded"
                                    />
                                </div>

                                <div className="w-1/4">
                                    <label className="text-white mb-1 block">VAT</label>
                                    <input
                                        id="vat"
                                        type="number"
                                        placeholder="VAT"
                                        value={newProduct.vat ? newProduct.vat : ''}
                                        onChange={(e) => {
                                            const updatedVAT = parseFloat(e.target.value) || 0;
                                            setNewProduct(prevProduct => ({ ...prevProduct, vat: updatedVAT }));
                                        }}
                                        className="block w-full p-2 bg-gray-700 text-white rounded"
                                    />
                                </div>
                            </div>

                            {/* Cost, Profit, and Profit Percentage in the next line with colors */}
                            <div className="flex space-x-4">
                                <div className="w-1/3 text-center bg-gray-700 text-red-400 p-4 rounded-lg">
                                    <h4 className="text-lg">Cost</h4>
                                    <p className="text-2xl">{newProduct.cost ? `${newProduct.cost} kr` : '0 kr'}</p>
                                </div>

                                <div className="w-1/3 text-center bg-gray-700 text-green-400 p-4 rounded-lg">
                                    <h4 className="text-lg">Profit</h4>
                                    <p className="text-2xl">
                                        {newProduct.price
                                            ? `${((newProduct.price - newProduct.cost) / (1 + newProduct.vat / 100)).toFixed(2)} kr`
                                            : '0 kr'}
                                    </p>
                                </div>

                                <div className="w-1/3 text-center bg-gray-700 text-blue-400 p-4 rounded-lg">
                                    <h4 className="text-lg">Profit %</h4>
                                    <p className="text-2xl">{profitPercentage}%</p>
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        <textarea
                            placeholder="Description"
                            value={newProduct.description}
                            onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                            className="block w-full mb-4 p-2 bg-gray-700 text-white rounded"
                        ></textarea>

                        {/* Image Dropzone and Preview */}
                        <div className="flex space-x-4 mb-4">
                            <div {...getRootProps()} className="border-2 border-dashed p-4 text-center w-1/2">
                                <input {...getInputProps()} />
                                {isDragActive ? (
                                    <p>Drop the files here ...</p>
                                ) : (
                                    <p>Drag 'n' drop an image, or click to select one</p>
                                )}
                            </div>
                            {previewImage && <img src={previewImage} alt="Product Preview" className="w-32 h-32 mb-4" />}
                        </div>

                        {/* Articles Section */}
                        <label className="text-white mb-1 block">Articles</label>
                        <div className="flex space-x-2 mb-2">
                            <Select
                                value={selectedArticle}
                                onChange={handleSelectArticle}
                                options={articles.map(article => ({
                                    value: article._id,
                                    label: article.name,
                                }))}
                                className="block w-1/2 bg-gray-700 text-white rounded"
                                placeholder="Search Article..."
                                theme={(theme) => ({
                                    ...theme,
                                    colors: {
                                        ...theme.colors,
                                        primary25: '#4B5563',
                                        primary: '#10B981',
                                        neutral0: '#1F2937',
                                        neutral80: '#F9FAFB',
                                    },
                                })}
                            />
                            <input
                                type="number"
                                placeholder="Quantity"
                                value={quantity}
                                onChange={(e) => setQuantity(e.target.value)}
                                className="block w-1/4 p-2 bg-gray-700 text-white rounded"
                            />
                            {selectedUnit && <span className="text-white">{selectedUnit}</span>}
                            <button
                                onClick={handleAddArticle}
                                className="bg-green-600 px-4 py-2 rounded hover:bg-green-700"
                            >
                                Add
                            </button>
                        </div>

                        {/* Display Added Articles */}
                        <div className="flex flex-wrap">
                            {newProduct.articles.map((articleObj, index) => {
                                const article =
                                    typeof articleObj.article === 'object'
                                        ? articleObj.article
                                        : articles.find(art => art._id === articleObj.article);

                                const showExclamation = !article?.price;

                                return (
                                    <div key={index} className="bg-gray-700 text-white p-2 rounded-full m-1 flex items-center">
                                        <span>
                                            {article ? `${article.name} - ${articleObj.quantity} ${article.unit}` : 'Article not found'}
                                        </span>
                                        {showExclamation && (
                                            <span className="ml-2 text-yellow-500" title="Article price is missing">⚠️</span>
                                        )}
                                        <button
                                            onClick={() => handleRemoveArticle(articleObj.article)}
                                            className="ml-2 bg-red-600 rounded-full w-6 h-6 flex items-center justify-center"
                                        >
                                            X
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Create/Update and Cancel Buttons */}
                        <button onClick={handleCreateOrUpdate} className="bg-green-600 px-4 py-2 rounded hover:bg-green-700">
                            {newProduct._id ? 'Update Product' : 'Create Product'}
                        </button>
                        <button onClick={closeDialog} className="bg-red-600 px-4 py-2 ml-2 rounded hover:bg-red-700">
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

};

export default Products;
